import { IColor, IVector3 } from '../../../../../api.js';
export class ElementFactory {
    static create_container(label, config = {}) {
        const { is_collapsed } = config;
        const collapsed_label = '▶ ' + label;
        const uncollapsed_label = '▼ ' + label;
        const tool_container = ElementFactory.create_element(/* html */ `
      <div class="container">
        <label class="container-label">${is_collapsed ? collapsed_label : uncollapsed_label}</label>
        <div class="container-content"></div>
      </div>
    `);
        const container_label = tool_container.querySelector('.container-label');
        const container_content = tool_container.querySelector('.container-content');
        if (is_collapsed) {
            container_content.style.display = 'none';
        }
        container_label.addEventListener('click', () => {
            if (container_content.style.display === 'none') {
                container_content.style.display = 'flex';
                container_label.innerText = uncollapsed_label;
            }
            else {
                container_content.style.display = 'none';
                container_label.innerText = collapsed_label;
            }
        });
        return {
            element: tool_container,
            content: container_content
        };
    }
    static create_button(label, config) {
        const { on_click } = config;
        const button = document.createElement('button');
        button.textContent = label;
        button.classList.add('tool', 'custom-button');
        button.addEventListener('click', on_click);
        return {
            element: button,
            set_enabled: (is_enabled) => {
                button.disabled = !is_enabled;
            }
        };
    }
    static create_label(label) {
        const label_element = document.createElement('label');
        label_element.appendChild(document.createTextNode(label));
        label_element.classList.add('custom-label');
        return {
            element: label_element
        };
    }
    static create_input(type, label, initial_value, config = {}) {
        const { on_change } = config;
        const tool_container = ElementFactory.create_element(/* html */ `
      <div class="tool">
        <input type="${type}" class="${type}" ${!on_change ? 'disabled' : ''}>
        <label>${label}</label>
        <div class="tracker"></div>
      </div>
    `);
        const input = tool_container.querySelector(`.${type}`);
        const tracker = tool_container.querySelector('.tracker');
        ElementFactory.set_input_value(input, initial_value, type, !!on_change);
        let current_value = initial_value;
        input.onchange = () => {
            current_value = ElementFactory.get_input_value(input, type);
            if (on_change)
                on_change(current_value);
        };
        if (type === 'number') {
            const drag_threshold = 5;
            input.addEventListener('mousedown', function (e) {
                let is_dragging = false;
                const start_x = e.pageX;
                const start_value = parseInt(input.value) || 0;
                const pointer_move = (e) => {
                    const diff_x = e.pageX - start_x;
                    if (!is_dragging && Math.abs(diff_x) > drag_threshold) {
                        is_dragging = true;
                    }
                    if (is_dragging) {
                        const new_value = start_value + diff_x * 0.1;
                        const rounded = Math.round(new_value * 10) / 10;
                        input.value = String(rounded);
                        current_value = rounded;
                        if (on_change)
                            on_change(current_value);
                    }
                };
                const pointer_down = () => {
                    if (!is_dragging) {
                        input.select();
                    }
                    document.removeEventListener('mousemove', pointer_move);
                    document.removeEventListener('mouseup', pointer_down);
                };
                document.addEventListener('mousemove', pointer_move);
                document.addEventListener('mouseup', pointer_down);
            });
        }
        let tracker_enabled = false;
        tracker.addEventListener('click', () => {
            tracker_enabled = !tracker_enabled;
            if (tracker_enabled) {
                tracker.classList.add('tracking-enabled');
            }
            else {
                tracker.classList.remove('tracking-enabled');
            }
        });
        return {
            element: tool_container,
            handler: {
                get: () => current_value,
                set: (val, report = true) => {
                    if (current_value !== val) {
                        current_value = val;
                        if (tracker_enabled) {
                            console.info('[TRACKING]:', label, ' > ', val);
                        }
                        ElementFactory.set_input_value(input, current_value, type, !!on_change);
                        if (report && on_change)
                            on_change(val);
                    }
                }
            },
            set_enabled: (is_enabled) => {
                input.disabled = !is_enabled;
            }
        };
    }
    static create_vector3(label, initial_value, config = {}) {
        var _a, _b, _c;
        const { on_change, is_collapsed } = config;
        let stored_value = initial_value ? IVector3.copy(initial_value) : initial_value;
        const report = (axis, value) => {
            if (!stored_value)
                return;
            stored_value[axis] = value;
            if (on_change) {
                on_change(IVector3.copy(stored_value));
            }
        };
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const x = ElementFactory.create_input('number', 'x', (_a = stored_value === null || stored_value === void 0 ? void 0 : stored_value.x) !== null && _a !== void 0 ? _a : 0, { on_change: (v) => report('x', v) });
        const y = ElementFactory.create_input('number', 'y', (_b = stored_value === null || stored_value === void 0 ? void 0 : stored_value.y) !== null && _b !== void 0 ? _b : 0, { on_change: (v) => report('y', v) });
        const z = ElementFactory.create_input('number', 'z', (_c = stored_value === null || stored_value === void 0 ? void 0 : stored_value.z) !== null && _c !== void 0 ? _c : 0, { on_change: (v) => report('z', v) });
        const optional = ElementFactory.create_input('string', 'value', String(initial_value), { is_nullable: true });
        if (!stored_value) {
            hide(x.element, y.element, z.element);
        }
        else {
            hide(optional.element);
        }
        if (!on_change) {
            x.set_enabled(false);
            y.set_enabled(false);
            z.set_enabled(false);
        }
        content.appendChild(x.element);
        content.appendChild(y.element);
        content.appendChild(z.element);
        content.appendChild(optional.element);
        return {
            element: element,
            handler: {
                get: () => {
                    if (!stored_value)
                        return stored_value;
                    return new IVector3(x.handler.get(), y.handler.get(), z.handler.get());
                },
                set: (val, report = true) => {
                    if (!val) {
                        if (stored_value === val)
                            return;
                        stored_value = val;
                        optional.handler.set(val);
                        show(optional.element);
                        hide(x.element, y.element, z.element);
                    }
                    else {
                        if (stored_value && IVector3.equals(val, stored_value))
                            return;
                        if (!stored_value)
                            stored_value = new IVector3();
                        IVector3.copy_to_ref(val, stored_value);
                        x.handler.set(val.x, false);
                        y.handler.set(val.y, false);
                        z.handler.set(val.z, false);
                        optional.handler.set(IVector3.to_string(val));
                        hide(optional.element);
                        show(x.element, y.element, z.element);
                    }
                    if (report && on_change)
                        on_change(val);
                }
            },
            set_enabled: (is_enabled) => {
                x.set_enabled(is_enabled);
                y.set_enabled(is_enabled);
                z.set_enabled(is_enabled);
            }
        };
    }
    static create_color_picker(label, initial_value, config = {}) {
        const { on_change } = config;
        const tool_container = ElementFactory.create_element(/* html */ `
      <div class="tool">
        <input type="color" class="color" value="${to_hex(initial_value)}" ${!on_change ? 'disabled' : ''}>
        <label>${label}</label>
        <div class="tracker"></div>
      </div>
    `);
        const input = tool_container.querySelector('.color');
        const tracker = tool_container.querySelector('.tracker');
        if (on_change) {
            input.onchange = () => {
                on_change(IColor.from_HEX(input.value));
            };
        }
        let tracker_enabled = false;
        tracker.addEventListener('click', () => {
            tracker_enabled = !tracker_enabled;
            if (tracker_enabled) {
                tracker.classList.add('tracking-enabled');
            }
            else {
                tracker.classList.remove('tracking-enabled');
            }
        });
        return {
            element: tool_container,
            handler: {
                get: () => IColor.from_HEX(input.value),
                set: (val, report = true) => {
                    const new_val = to_hex(val);
                    if (input.value !== new_val) {
                        if (tracker_enabled) {
                            console.info('[TRACKING]:', label, ' > ', val);
                        }
                        input.value = new_val;
                        if (report && on_change)
                            on_change(val);
                    }
                }
            },
            set_enabled: (is_enabled) => {
                input.disabled = !is_enabled;
            }
        };
    }
    static create_color3(label, initial_value, config = {}) {
        var _a, _b, _c;
        const { on_change, is_collapsed, has_color_picker } = config;
        let stored_value = initial_value ? IColor.copy(initial_value) : initial_value;
        let picker;
        const report = (channel, value) => {
            if (!stored_value)
                return;
            stored_value[channel] = value;
            if (on_change) {
                on_change(IColor.copy(stored_value));
            }
            if (picker) {
                picker.handler.set(stored_value);
            }
        };
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const r = ElementFactory.create_input('number', 'r', (_a = stored_value === null || stored_value === void 0 ? void 0 : stored_value.r) !== null && _a !== void 0 ? _a : 0, { on_change: (v) => report('r', v) });
        const g = ElementFactory.create_input('number', 'g', (_b = stored_value === null || stored_value === void 0 ? void 0 : stored_value.g) !== null && _b !== void 0 ? _b : 0, { on_change: (v) => report('g', v) });
        const b = ElementFactory.create_input('number', 'b', (_c = stored_value === null || stored_value === void 0 ? void 0 : stored_value.b) !== null && _c !== void 0 ? _c : 0, { on_change: (v) => report('b', v) });
        const optional = ElementFactory.create_input('string', 'value', String(initial_value), { is_nullable: true });
        if (!on_change) {
            r.set_enabled(false);
            g.set_enabled(false);
            b.set_enabled(false);
        }
        content.appendChild(r.element);
        content.appendChild(g.element);
        content.appendChild(b.element);
        if (has_color_picker !== false) {
            picker = ElementFactory.create_color_picker('', initial_value !== null && initial_value !== void 0 ? initial_value : new IColor(), {
                on_change: (value) => {
                    if (!stored_value)
                        stored_value = new IColor();
                    IColor.copy_to_ref(value, stored_value);
                    r.handler.set(value.r, false);
                    g.handler.set(value.g, false);
                    b.handler.set(value.b, false);
                    if (on_change) {
                        on_change(IColor.copy(stored_value));
                    }
                }
            });
            content.appendChild(picker.element);
        }
        content.appendChild(optional.element);
        if (!stored_value) {
            hide(r.element, g.element, b.element);
            if (picker) {
                hide(picker.element);
            }
        }
        else {
            hide(optional.element);
        }
        return {
            element: element,
            handler: {
                get: () => {
                    if (!stored_value)
                        return stored_value;
                    return new IColor(r.handler.get(), g.handler.get(), b.handler.get());
                },
                set: (val, report = true) => {
                    if (!val) {
                        if (stored_value === val)
                            return;
                        stored_value = val;
                        optional.handler.set(val);
                        show(optional.element);
                        hide(r.element, g.element, b.element);
                        if (picker) {
                            hide(picker.element);
                        }
                    }
                    else {
                        if (stored_value && IColor.equals(val, stored_value))
                            return;
                        if (!stored_value)
                            stored_value = new IColor();
                        IColor.copy_to_ref(val, stored_value);
                        r.handler.set(val.r, false);
                        g.handler.set(val.g, false);
                        b.handler.set(val.b, false);
                        optional.handler.set(IColor.to_string(val));
                        hide(optional.element);
                        show(r.element, g.element, b.element);
                        if (picker) {
                            picker.handler.set(val, false);
                            show(picker.element);
                        }
                    }
                    if (report && on_change)
                        on_change(val);
                }
            },
            set_enabled: (is_enabled) => {
                r.set_enabled(is_enabled);
                g.set_enabled(is_enabled);
                b.set_enabled(is_enabled);
                picker === null || picker === void 0 ? void 0 : picker.set_enabled(is_enabled);
            }
        };
    }
    static create_select_menu(label, options, initial_value, config = {}) {
        const { on_change } = config;
        const tool_container = ElementFactory.create_element(/* html */ `
      <div class="tool">
        <select class="select" ${!on_change ? 'disabled' : ''}></select>
        <label>${label}</label>
        <div class="tracker"></div>
      </div>
    `);
        const select = tool_container.querySelector('.select');
        const tracker = tool_container.querySelector('.tracker');
        options.forEach(option => {
            const option_element = document.createElement('option');
            option_element.value = option;
            option_element.textContent = option;
            if (option === initial_value) {
                option_element.selected = true;
            }
            select.appendChild(option_element);
        });
        if (on_change) {
            select.addEventListener('change', () => {
                on_change(select.value);
            });
        }
        let tracker_enabled = false;
        tracker.addEventListener('click', () => {
            tracker_enabled = !tracker_enabled;
            if (tracker_enabled) {
                tracker.classList.add('tracking-enabled');
            }
            else {
                tracker.classList.remove('tracking-enabled');
            }
        });
        return {
            element: tool_container,
            handler: {
                get: () => select.value,
                set: (val, report = true) => {
                    if (select.value !== val) {
                        if (tracker_enabled) {
                            console.info('[TRACKING]:', label, ' > ', val);
                        }
                        select.value = val;
                        if (report && on_change)
                            on_change(val);
                    }
                }
            },
            set_enabled: (is_enabled) => {
                select.disabled = !is_enabled;
            }
        };
    }
    static create_setter(type, label, initial_value, setter, config = {}) {
        var _a;
        const { value_label, button_text, is_collapsed } = config;
        const container = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const input = ElementFactory.create_input(type, value_label !== null && value_label !== void 0 ? value_label : 'value', initial_value);
        (_a = input.set_enabled) === null || _a === void 0 ? void 0 : _a.call(input, true);
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Set', { on_click: () => setter(input.handler.get()) });
        container.content.appendChild(input.element);
        container.content.appendChild(button.element);
        return {
            element: container.element,
            set_enabled: (is_enabled) => {
                input.set_enabled(is_enabled);
                button.set_enabled(is_enabled);
            }
        };
    }
    static create_getter(type, label, initial_value, getter, config = {}) {
        const { value_label: value_text, button_text, is_collapsed } = config;
        const container = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const input = ElementFactory.create_input(type, value_text !== null && value_text !== void 0 ? value_text : 'value', initial_value);
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Get', { on_click: () => input.handler.set(getter()) });
        container.content.appendChild(input.element);
        container.content.appendChild(button.element);
        return {
            element: container.element,
            set_enabled: (is_enabled) => {
                var _a;
                (_a = button.set_enabled) === null || _a === void 0 ? void 0 : _a.call(button, is_enabled);
            }
        };
    }
    static create_vector3_setter(label, initial_value, setter, config) {
        const { button_text, is_collapsed } = config;
        const stored_value = IVector3.copy(initial_value);
        const report = (axis, value) => {
            stored_value[axis] = value;
        };
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const x = ElementFactory.create_input('number', 'x', stored_value.x, { on_change: (v) => report('x', v) });
        const y = ElementFactory.create_input('number', 'y', stored_value.y, { on_change: (v) => report('y', v) });
        const z = ElementFactory.create_input('number', 'z', stored_value.z, { on_change: (v) => report('z', v) });
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Set', { on_click: () => setter(stored_value) });
        content.appendChild(x.element);
        content.appendChild(y.element);
        content.appendChild(z.element);
        content.appendChild(button.element);
        return {
            element: element,
            set_enabled: (is_enabled) => {
                x.set_enabled(is_enabled);
                y.set_enabled(is_enabled);
                z.set_enabled(is_enabled);
                button.set_enabled(is_enabled);
            }
        };
    }
    static create_vector3_getter(label, initial_value, getter, config = {}) {
        const { button_text, is_collapsed } = config;
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const x = ElementFactory.create_input('number', 'r', initial_value.x);
        const y = ElementFactory.create_input('number', 'g', initial_value.y);
        const z = ElementFactory.create_input('number', 'b', initial_value.z);
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Get', {
            on_click: () => {
                const value = getter();
                x.handler.set(value.x);
                y.handler.set(value.y);
                z.handler.set(value.z);
            }
        });
        content.appendChild(x.element);
        content.appendChild(y.element);
        content.appendChild(z.element);
        content.appendChild(button.element);
        return {
            element: element,
            set_enabled: (is_enabled) => {
                button.set_enabled(is_enabled);
            }
        };
    }
    static create_color3_setter(label, initial_value, setter, config = {}) {
        const { has_color_picker, button_text, is_collapsed } = config;
        const stored_value = IColor.copy(initial_value);
        let picker;
        const report = (channel, value) => {
            stored_value[channel] = value;
            if (picker) {
                picker.handler.set(stored_value);
            }
        };
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const r = ElementFactory.create_input('number', 'r', stored_value.r, { on_change: (v) => report('r', v) });
        const g = ElementFactory.create_input('number', 'g', stored_value.g, { on_change: (v) => report('g', v) });
        const b = ElementFactory.create_input('number', 'b', stored_value.b, { on_change: (v) => report('b', v) });
        content.appendChild(r.element);
        content.appendChild(g.element);
        content.appendChild(b.element);
        if (has_color_picker !== false) {
            picker = ElementFactory.create_color_picker('color', initial_value, {
                on_change: (value) => {
                    IColor.copy_to_ref(value, stored_value);
                    r.handler.set(value.r, false);
                    g.handler.set(value.g, false);
                    b.handler.set(value.b, false);
                }
            });
            content.appendChild(picker.element);
        }
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Set', { on_click: () => setter(stored_value) });
        content.appendChild(button.element);
        return {
            element: element,
            set_enabled: (is_enabled) => {
                r.set_enabled(is_enabled);
                g.set_enabled(is_enabled);
                b.set_enabled(is_enabled);
                picker === null || picker === void 0 ? void 0 : picker.set_enabled(is_enabled);
                button.set_enabled(is_enabled);
            }
        };
    }
    static create_color3_getter(label, initial_value, getter, config = {}) {
        const { has_color_picker, button_text, is_collapsed } = config;
        let picker;
        const { element, content } = ElementFactory.create_container(label, { is_collapsed: is_collapsed });
        const r = ElementFactory.create_input('number', 'r', initial_value.r);
        const g = ElementFactory.create_input('number', 'g', initial_value.g);
        const b = ElementFactory.create_input('number', 'b', initial_value.b);
        content.appendChild(r.element);
        content.appendChild(g.element);
        content.appendChild(b.element);
        if (has_color_picker !== false) {
            picker = ElementFactory.create_color_picker('color', initial_value);
            content.appendChild(picker.element);
        }
        const button = ElementFactory.create_button(button_text !== null && button_text !== void 0 ? button_text : 'Get', {
            on_click: () => {
                const value = getter();
                r.handler.set(value.r);
                g.handler.set(value.g);
                b.handler.set(value.b);
                if (picker) {
                    picker.handler.set(value, false);
                }
            }
        });
        content.appendChild(button.element);
        return {
            element: element,
            set_enabled: (is_enabled) => {
                button.set_enabled(is_enabled);
            }
        };
    }
    static set_input_value(input, value, original_type, is_readonly) {
        if (value === undefined || value === null) {
            input.classList.add('null');
            input.type = 'string';
            input.value = String(value);
            input.disabled = true;
        }
        else {
            if (input.type !== original_type) {
                input.classList.remove('null');
                input.type = original_type;
                input.disabled = !is_readonly;
            }
            if (typeof value === 'boolean') {
                input.checked = value;
            }
            else {
                input.value = String(value);
            }
        }
    }
    static get_input_value(input, type) {
        if (input.type !== type) {
            return null;
        }
        switch (type) {
            case 'string':
                return input.value;
            case 'number':
                return Number.parseFloat(input.value);
            case 'checkbox':
                return input.checked;
        }
        throw new Error('Unsupported type: ' + type);
    }
    static create_element(html) {
        const doc = ElementFactory._parser.parseFromString(html, 'text/html');
        return doc.body.firstChild;
    }
}
ElementFactory._parser = new DOMParser();
function to_hex(color) {
    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);
    const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    return `#${hex}`;
}
function show(...elements) {
    elements.forEach(e => { e.style.display = 'flex'; });
}
function hide(...elements) {
    elements.forEach(e => { e.style.display = 'none'; });
}
//# sourceMappingURL=ElementFactory.js.map